import {
  AssetMediaRef,
  QuestionnaireCollectedAssetOperation,
  QuestionnaireCollectedItem,
  QuestionnaireValidationIssue,
  TestedAssetType,
  testedAssetUnusedAssetIds,
} from "@digital-twins/shared-types";

import { ChatMessageCollect } from "../types";
import type { ChatMessageCollectAttachItem } from "@digital-twins/shared-types";

/**
 * Pure helpers for chat collect cards (composer-as-forms): what a card's
 * receipt names, where an attach card's files go, and the media a collected
 * file carries. The new assets themselves are the server's: the builder sends
 * one `collect_assets` operation (D2 S5.4).
 */

export interface CollectedAssetMediaRef extends AssetMediaRef {
  clientPreviewMediaRefs?: Array<AssetMediaRef>;
}

/** A collect's links as the builder sends them: trimmed, and a blank one skipped. */
export function collectableLinks(urls: ReadonlyArray<string>): Array<string> {
  return urls.map((url) => url.trim()).filter(Boolean);
}

/**
 * The `collect_assets` operation a collect sends. A recipe may cap one card's
 * collection (`maxAssets`): past it, nothing is accepted and nothing is sent.
 */
export function collectAssetsOperation(
  collect: Pick<ChatMessageCollect, "assetType" | "maxAssets">,
  items: Array<QuestionnaireCollectedItem>,
): QuestionnaireCollectedAssetOperation {
  if (collect.maxAssets !== undefined && items.length > collect.maxAssets) {
    throw new Error(
      "Collected assets exceed the declared recipe limit; no assets were accepted",
    );
  }
  return {
    op: "collect_assets",
    assetType: collect.assetType as TestedAssetType,
    items,
  };
}

/** A collected file's media as stored: the file, then its issued preview. */
export function persistableCollectedMedia(
  mediaRef: CollectedAssetMediaRef,
): Array<AssetMediaRef> {
  const { clientPreviewMediaRefs, ...file } = mediaRef;
  return [file, ...(clientPreviewMediaRefs ?? [])];
}

type AssetRecord = Record<string, unknown>;
type TestedAssetsBlock = Record<string, unknown>;

function assetsOf(block: TestedAssetsBlock | null | undefined): Array<AssetRecord> {
  const assets = (block as { assets?: unknown } | null | undefined)?.assets;
  return Array.isArray(assets) ? (assets as Array<AssetRecord>) : [];
}

function assetId(asset: AssetRecord): string {
  return String(asset.id ?? "").trim();
}

/**
 * The assets a collect card has collected while its question is open: the
 * saved assets of the type it asks for (plan S4.1). Its receipt names them,
 * whichever write saved them: the card's own submit, a submit whose receipt
 * turn failed or was stopped, or the user's Save of assets added in the panel.
 */
export function collectedAssetKeysOf(
  savedAssets: unknown,
  assetType: string,
): Array<string> {
  return assetsOf(savedAssets as TestedAssetsBlock | null | undefined)
    .filter((asset) => asset.type === assetType)
    .map((asset) => String(asset.stableKey ?? ""))
    .filter(Boolean);
}

function assetHasMedia(asset: AssetRecord | undefined): boolean {
  return Array.isArray(asset?.media) && (asset.media as Array<unknown>).length > 0;
}

function assetsByKey(savedAssets: unknown): Map<string, AssetRecord> {
  return new Map(
    assetsOf(savedAssets as TestedAssetsBlock | null | undefined).map(
      (asset) => [String(asset.stableKey ?? ""), asset],
    ),
  );
}

/**
 * Whether an attach card's item has its file (F19): every asset of it (each
 * price version of a product) holds one.
 */
function attachItemHasFile(
  byKey: ReadonlyMap<string, AssetRecord>,
  item: ChatMessageCollectAttachItem,
): boolean {
  return item.assetKeys.every((key) => assetHasMedia(byKey.get(key)));
}

/**
 * The items an attach card still offers (F19): those of its items without
 * their file, in the card's order. A file staged on the card goes to one of
 * them; the ones that have theirs count as collected.
 */
export function openAttachItemsOf(
  savedAssets: unknown,
  collect: Pick<ChatMessageCollect, "attachTo">,
): Array<ChatMessageCollectAttachItem> {
  const byKey = assetsByKey(savedAssets);
  return (collect.attachTo ?? []).filter(
    (item) => !attachItemHasFile(byKey, item),
  );
}

/**
 * What a collect card's receipt names while its question is open. A card
 * whose files are the items: the saved assets of its type. An attach card
 * (F19): the assets of its items that have their file, which is none when the
 * user continued without one.
 */
export function receiptAssetKeysOf(
  savedAssets: unknown,
  collect: Pick<ChatMessageCollect, "assetType" | "attachTo">,
): Array<string> {
  if (!collect.attachTo) {
    return collectedAssetKeysOf(savedAssets, collect.assetType);
  }
  const byKey = assetsByKey(savedAssets);
  return collect.attachTo
    .filter((item) => attachItemHasFile(byKey, item))
    .flatMap((item) => item.assetKeys);
}

/** How many of an attach card's items have their file (F19). */
export function attachItemsWithFileOf(
  savedAssets: unknown,
  collect: Pick<ChatMessageCollect, "attachTo">,
): number {
  const byKey = assetsByKey(savedAssets);
  return (collect.attachTo ?? []).filter((item) =>
    attachItemHasFile(byKey, item),
  ).length;
}

/**
 * Which item each file goes on (F19): the item the user put it on, else the
 * next item without a file, in order. Only an item still without its file in
 * the saved block takes one, so a file added in between (the panel, another
 * tab) is never replaced; a file for any other item is not placed.
 */
export function attachPlacementsOf(
  savedAssets: unknown,
  collect: Pick<ChatMessageCollect, "attachTo">,
  mediaRefs: ReadonlyArray<CollectedAssetMediaRef>,
  targets?: ReadonlyArray<ChatMessageCollectAttachItem>,
): Array<{ item: ChatMessageCollectAttachItem; mediaRef: CollectedAssetMediaRef }> {
  const open = openAttachItemsOf(savedAssets, collect);
  const isOpen = (item: ChatMessageCollectAttachItem) =>
    open.some((candidate) => candidate.assetKeys.join("\n") === item.assetKeys.join("\n"));
  return mediaRefs.flatMap((mediaRef, index) => {
    const item = targets?.[index] ?? open[index];
    return item && isOpen(item) ? [{ item, mediaRef }] : [];
  });
}

/**
 * An attach card's files on its items (F19): each file becomes the media of
 * every asset of the item it was placed on (each price version of a product
 * alike). Nothing is added, renamed or re-described, so the write is only
 * those assets' media.
 */
export function attachCollectedMedia(
  block: TestedAssetsBlock,
  placements: ReadonlyArray<{
    item: ChatMessageCollectAttachItem;
    mediaRef: CollectedAssetMediaRef;
  }>,
): TestedAssetsBlock {
  // The files placed on an item are its media, in order: the card places no
  // more on one item than it holds (`FILES_PER_ASSET`).
  const assets = assetsOf(block).map((asset) => {
    const placed = placements.filter(({ item }) =>
      item.assetKeys.includes(String(asset.stableKey ?? "")),
    );
    return placed.length === 0
      ? { ...asset }
      : {
          ...asset,
          media: placed.flatMap(({ mediaRef }) =>
            persistableCollectedMedia(mediaRef),
          ),
        };
  });
  return { ...block, assets };
}

/**
 * Which item got which file, for an attach card's receipt (F19): each of the
 * card's items the receipt names that has its file, with the name of the file
 * its first asset shows (the upload's own name), or null.
 */
export function attachReceiptLinesOf(
  savedAssets: unknown,
  collect: Pick<ChatMessageCollect, "attachTo">,
  assetKeys: ReadonlyArray<string>,
): Array<{ item: string; file: string | null }> {
  const byKey = assetsByKey(savedAssets);
  return (collect.attachTo ?? [])
    .filter(
      (item) =>
        item.assetKeys.some((key) => assetKeys.includes(key)) &&
        attachItemHasFile(byKey, item),
    )
    .map((item) => {
      const media = byKey.get(item.assetKeys[0])?.media as
        | Array<{ sliceOf?: unknown; label?: unknown; altText?: unknown }>
        | undefined;
      const primary = media?.find((ref) => !ref.sliceOf);
      const file = primary?.label ?? primary?.altText;
      return {
        item: item.label,
        file: typeof file === "string" && file.trim() ? file.trim() : null,
      };
    });
}

/** The stable keys of every asset a tested-asset block holds. */
export function assetKeysOf(savedAssets: unknown): Array<string> {
  return assetsOf(savedAssets as TestedAssetsBlock | null | undefined)
    .map((asset) => String(asset.stableKey ?? ""))
    .filter(Boolean);
}

/**
 * Of the assets a write just added (`addedKeys`), the ones no question shows
 * yet (04 I-19): what the "update the questions?" card offers to bring in.
 * The write's readiness is the verdict: it names such an asset
 * `tested_asset_unused`, and never an asset a per-version binding, a
 * tested-asset grid or a typed roster already takes in, nor any asset of a
 * draft with no questions.
 */
export function uncoveredAssetsAmong(
  addedKeys: ReadonlyArray<string>,
  accepted: {
    draft: unknown;
    readiness?: { blockingIssues: Array<QuestionnaireValidationIssue> } | null;
  },
): Array<{ stableKey: string; type: string }> {
  const unused = testedAssetUnusedAssetIds(
    accepted.readiness?.blockingIssues ?? [],
  );
  const added = new Set(addedKeys);
  return assetsOf(
    (accepted.draft as { testedAssets?: TestedAssetsBlock } | null)
      ?.testedAssets,
  )
    .filter(
      (asset) =>
        added.has(String(asset.stableKey ?? "")) && unused.has(assetId(asset)),
    )
    .map((asset) => ({
      stableKey: String(asset.stableKey),
      type: String(asset.type ?? ""),
    }));
}

/**
 * A `collect_assets` write's replay rule: the draft already holds every item
 * it collected (a file by its media id, a link by its address), so a lost
 * first response was this write's.
 */
export function carriesCollectedItems(
  items: ReadonlyArray<QuestionnaireCollectedItem>,
): (latest: { testedAssets?: unknown }) => boolean {
  return (latest) => {
    const assets = assetsOf(latest.testedAssets as TestedAssetsBlock | null);
    const mediaIds = new Set(
      assets.flatMap((asset) =>
        Array.isArray(asset.media)
          ? (asset.media as Array<{ id?: unknown }>).map((ref) => String(ref.id))
          : [],
      ),
    );
    const links = new Set(
      assets.flatMap((asset) =>
        [asset.pageUrl, asset.url].map((url) =>
          String(url ?? "").trim().toLowerCase(),
        ),
      ),
    );
    return items.every((item) =>
      "media" in item
        ? mediaIds.has(item.media[0].id)
        : links.has(item.url.trim().toLowerCase()),
    );
  };
}
